import { useCallback, useEffect, useMemo, useState } from 'react'
import { postNamedQuery } from '../../../../service'
import { useParameterReplacementEx } from '../../../../hooks/useAbundanceEngineParameters'
import { useVariables } from '../../../../providers/VariableProvider'
import useBlueprintData from './useBlueprintData'

/**
 * Build the parameter object for a child level by mapping parent row values
 * through the level's paramMapping config.
 *
 * @param {Object} parentRow - the parent row data
 * @param {Object} paramMapping - { paramName: parentColumnName }
 * @param {Object} baseParameters - runtime parameters from the presentation
 */
function buildChildParameters (parentRow, paramMapping, baseParameters) {
  const mapped = {}
  for (const [paramName, columnName] of Object.entries(paramMapping || {})) {
    mapped[paramName] = parentRow[columnName]
  }
  return { ...baseParameters, ...mapped }
}

function findAndUpdateNode (tree, rowId, updater) {
  return tree.map((node, index) => {
    const nodeId = String(index)
    if (nodeId === rowId) {
      return { ...node, ...updater(node) }
    }
    if (rowId.startsWith(nodeId + '.') && node.subRows?.length) {
      const childId = rowId.slice(nodeId.length + 1)
      return {
        ...node,
        subRows: findAndUpdateNode(node.subRows, childId, updater)
      }
    }
    return node
  })
}

function buildExpandState (tree, maxDepth, currentDepth = 0) {
  const state = {}
  if (currentDepth >= maxDepth) return state
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.subRows?.length) {
      const id = String(i)
      state[id] = true
      const childState = buildExpandState(node.subRows, maxDepth, currentDepth + 1)
      for (const [childKey, val] of Object.entries(childState)) {
        state[`${id}.${childKey}`] = val
      }
    }
  }
  return state
}

/**
 * Lazy-loading hierarchical tree data hook for blueprint presentations.
 * Fetches top-level data declaratively via useBlueprintData, then loads
 * child levels imperatively on expand.
 *
 * Modeled after usePerformanceTreeData but using the blueprint execution API.
 *
 * TECH DEBT: Child-level fetches use postNamedQuery directly instead of React
 * Query. This means no query caching (re-expand after param change re-fetches),
 * no abundance engine variable resolution on child paramMapping values, and no
 * automatic stale/refetch behavior. The _subRowsFetched flag prevents redundant
 * fetches within a single tree lifecycle, and the tree is rebuilt entirely when
 * topData changes (discarding stale children). Migrating to React Query would
 * require an imperative query client approach since hooks can't be called
 * conditionally on expand.
 */
export default function useBlueprintLazyTreeData (blueprintCode, hierarchyConfig, options = {}) {
  const {
    parameters = {},
    latchParams = {}
  } = options

  const {
    levels = [],
    initialFetchDepth = 1,
    defaultExpandDepth = 0,
    labelColumn,
    leafLabelAccessor
  } = hierarchyConfig || {}

  const applyLabel = useCallback((row, levelIndex) => {
    if (!labelColumn) return
    const isLastLevel = levelIndex >= levels.length - 1
    if (isLastLevel && leafLabelAccessor) {
      row[labelColumn] = row[leafLabelAccessor] ?? null
    }
  }, [labelColumn, leafLabelAccessor, levels.length])

  const { variables } = useVariables()
  const [resolvedParams] = useParameterReplacementEx(parameters, variables)
  const effectiveParams = useMemo(() => {
    return { ...resolvedParams, ...latchParams }
  }, [resolvedParams, latchParams])

  const topLevel = levels[0]
  const topPurposeCode = topLevel?.purposeCode ?? null

  const { data: topData, isLoading: topLoading, isFetching: topFetching } = useBlueprintData(
    blueprintCode,
    topPurposeCode,
    { parameters: effectiveParams, pageSize: 1000 }
  )

  const [treeData, setTreeData] = useState([])
  const [isExpandLoading, setIsExpandLoading] = useState(false)

  useEffect(() => {
    if (topLoading || !topData) return
    let stale = false

    const initialTree = topData.map(row => {
      const node = {
        ...row,
        _depth: 0,
        _levelIndex: 0,
        _subRowsFetched: false,
        _subRowsFetching: false,
        subRows: []
      }
      applyLabel(node, 0)
      return node
    })

    if (initialFetchDepth > 1 && levels.length > 1) {
      const fetchSecondLevel = async () => {
        const nextLevel = levels[1]
        const results = await Promise.all(
          initialTree.map(async (parentRow) => {
            const childParams = buildChildParameters(parentRow, nextLevel.paramMapping, effectiveParams)
            try {
              const { data } = await postNamedQuery('coreData', 'execute-query-blueprint', {
                code: blueprintCode,
                purposeCode: nextLevel.purposeCode,
                parameters: childParams,
                pageSize: 1000
              })
              const children = (data?.data || []).map(row => {
                const child = {
                  ...row,
                  _depth: 1,
                  _levelIndex: 1,
                  _subRowsFetched: false,
                  _subRowsFetching: false,
                  subRows: []
                }
                applyLabel(child, 1)
                return child
              })
              return { ...parentRow, subRows: children, _subRowsFetched: true }
            } catch {
              return parentRow
            }
          })
        )
        if (!stale) setTreeData(results)
      }
      fetchSecondLevel()
    } else {
      setTreeData(initialTree)
    }

    return () => { stale = true }
  }, [topData, topLoading, initialFetchDepth, levels, blueprintCode, effectiveParams, applyLabel])

  const expandState = useMemo(() => {
    return buildExpandState(treeData, defaultExpandDepth)
  }, [treeData, defaultExpandDepth])

  const [controlledExpandState, setControlledExpandState] = useState({})

  useEffect(() => {
    setControlledExpandState(expandState)
  }, [expandState])

  const onLevelExpand = useCallback(async (row) => {
    const rowId = row.id
    const original = row.original

    if (original._subRowsFetched) {
      setControlledExpandState(prev => ({
        ...prev,
        [rowId]: !prev[rowId]
      }))
      return
    }

    const nextLevelIndex = (original._levelIndex ?? 0) + 1
    if (nextLevelIndex >= levels.length) return

    const nextLevel = levels[nextLevelIndex]

    setTreeData(prev => findAndUpdateNode(prev, rowId, () => ({
      _subRowsFetching: true
    })))
    setIsExpandLoading(true)

    try {
      const childParams = buildChildParameters(original, nextLevel.paramMapping, effectiveParams)
      const { data } = await postNamedQuery('coreData', 'execute-query-blueprint', {
        code: blueprintCode,
        purposeCode: nextLevel.purposeCode,
        parameters: childParams,
        pageSize: 1000
      })
      const children = (data?.data || []).map(childRow => {
        const child = {
          ...childRow,
          _depth: nextLevelIndex,
          _levelIndex: nextLevelIndex,
          _subRowsFetched: false,
          _subRowsFetching: false,
          subRows: []
        }
        applyLabel(child, nextLevelIndex)
        return child
      })

      setTreeData(prev => findAndUpdateNode(prev, rowId, () => ({
        _subRowsFetching: false,
        _subRowsFetched: true,
        subRows: children
      })))

      setControlledExpandState(prev => ({
        ...prev,
        [rowId]: true
      }))
    } catch (error) {
      console.error('Failed to fetch child rows', error)
      setTreeData(prev => findAndUpdateNode(prev, rowId, () => ({
        _subRowsFetching: false
      })))
    } finally {
      setIsExpandLoading(false)
    }
  }, [levels, blueprintCode, effectiveParams, applyLabel])

  return {
    treeData,
    isLoading: topLoading || topFetching,
    isExpandLoading,
    expandState: controlledExpandState,
    setExpandState: setControlledExpandState,
    onLevelExpand
  }
}
